import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import {Injectable} from '@angular/core';

// am4core.useTheme(am4themes_animated);
am4core.addLicense('CH222128706');

@Injectable(
  {providedIn: 'root'}
)
export class AmchartsService {

  readonly FONT = '#222222';
  readonly BG = '#FAFAFA';
  readonly PRIMARY = '#3480D6';
  readonly ACCENT = '#e52dd3';
  readonly ERROR = '#FF0000';

  readonly GREEN = '#01912D';
  readonly GREEN_DARK = '#1c4409';
  readonly GREEN_LIGHT = '#3DCF35';
  readonly GREY = '#888888';
  readonly GREY_DARK = '#555555';
  readonly GREY_LIGHT = '#b3b3b3';
  readonly MAUVE = '#5D7498';
  readonly ORANGE = '#FF5722';
  readonly PINK = '#ff00d8';
  readonly PINK_BLUE = '#6771DC';
  readonly PINK_ORANGE = '#DC8C67';
  readonly PURSE = '#6C5F9F';
  readonly RED = '#ff0000';
  readonly RED_DARK = '#AA0000';
  readonly RED_LIGHT = '#ff4d4c';
  readonly RED_ORANGE = '#DC6967';
  readonly ROSE = '#DC6788';
  readonly YELLOW = '#ffff00';
  readonly YELLOW_GREEN = '#C3DC67';

  setChartDefaults(chart: am4charts.XYChart, withLegend = false, title = ''): void {
    chart.padding(10, 7, 0, 7);

    chart.preloader.background.fill = am4core.color(this.BG);

    chart.zoomOutButton.background.fill = am4core.color(this.FONT);
    chart.zoomOutButton.icon.stroke = am4core.color(this.BG);
    chart.zoomOutButton.background.states.getKey('hover').properties.fill = am4core.color(this.PRIMARY);

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = 'zoomX';
    chart.cursor.lineX.fill = am4core.color(this.FONT);
    chart.cursor.lineX.stroke = am4core.color(this.FONT);
    chart.cursor.lineY.fill = am4core.color(this.FONT);
    chart.cursor.lineY.stroke = am4core.color(this.FONT);
    chart.cursor.lineY.disabled = true;

    if (title) {
      const chartTitle = chart.titles.create();
      chartTitle.text = title;
      chartTitle.fontSize = 18;
    }

    if (withLegend) {
      chart.legend = new am4charts.Legend();
      chart.legend.position = 'top';
      const markerTemplate = chart.legend.markers.template;
      markerTemplate.width = 10;
      markerTemplate.height = 10;

      chart.legend.labels.template.fill = am4core.color(this.FONT);
      chart.legend.valueLabels.template.fill = am4core.color(this.FONT);

      chart.legend.labels.template.textDecoration = 'none';
      chart.legend.valueLabels.template.textDecoration = 'none';
      chart.legend.fontSize = '85%';

      const as = chart.legend.labels.template.states.getKey('active');
      as.properties.textDecoration = 'line-through';
      as.properties.fill = am4core.color(this.GREY);

      const as2 = chart.legend.valueLabels.template.states.getKey('active');
      as2.properties.textDecoration = 'line-through';
      as2.properties.fill = am4core.color(this.GREY);
    }

  }

  setCategoryAxis(axis: am4charts.CategoryAxis, category: string, withTooltip: boolean, withGrid: boolean, withLabels: boolean): void {
    this.setAxisTooltip(axis, withTooltip);
    this.setGenericAxisProps(axis, withGrid, withLabels);
    axis.dataFields.category = category;
    axis.renderer.minGridDistance = 1;
    axis.renderer.labels.template.location = 0.5; // 0 & 1 IS ON TICK, 0.5 CENTERS BETWEEN TICKS
  }

  setDateAxis(axis: am4charts.DateAxis, date: string, withTooltip: boolean, withGrid: boolean, withLabels: boolean, minGrid: number = null): void {
    this.setAxisTooltip(axis, withTooltip);
    this.setGenericAxisProps(axis, withGrid, withLabels);
    axis.dataFields.date = date;
    axis.renderer.labels.template.location = 0.001; // 0.5 CENTERS BETWEEN TICKS
    if (minGrid !== null) {
      axis.renderer.minGridDistance = minGrid;
    }
  }

  setValueAxis(axis: am4charts.ValueAxis, title: string = '', withTooltip: boolean = false, withGrid: boolean = true, withLabels: boolean = true): void {
    this.setAxisTooltip(axis, withTooltip);
    this.setGenericAxisProps(axis, withGrid, withLabels);
    axis.extraMax = .0;
    axis.extraMin = .0;
    axis.title.text = title;
  }

  private setAxisTooltip(axis: any, withToolTip: boolean, dy: number = 5): void {
    axis.tooltip.disabled = !withToolTip;
    if (withToolTip) {
      const axisTooltip = axis.tooltip;
      axisTooltip.background.fill = am4core.color(this.FONT);
      axisTooltip.label.fill = am4core.color(this.BG);
      axisTooltip.background.strokeWidth = 0;
      axisTooltip.background.cornerRadius = 4;
      axisTooltip.dy = dy;
    }
  }

  private setGenericAxisProps(
    axis: am4charts.CategoryAxis | am4charts.ValueAxis | am4charts.DateAxis,
    withGrid: boolean,
    withLabels: boolean
  ): void {
    axis.cursorTooltipEnabled = true;
    axis.renderer.fontSize = '1em';
    axis.renderer.grid.template.disabled = !withGrid;
    axis.renderer.grid.template.location = 0; // THIS ALSO IMPACTS TOOLTIP LOCATION !!
    axis.renderer.grid.template.stroke = am4core.color(this.FONT);
    axis.renderer.labels.template.disabled = !withLabels;
    axis.renderer.labels.template.fill = am4core.color(this.FONT);
    axis.renderer.minWidth = 10; // IMPACTS SPACE FOR Y-AXIS LABELS
    axis.title.fill = am4core.color(this.FONT);
  }

  setSeries(
    series: any,
    type: 'category' | 'date' | 'value',
    name: string,
    valueX: string,
    valueY: string,
    openValueY: string,
    color: string,
    defToolTipColor: boolean = false,
    tooltipText: string = null,
    strokeWidth: number = 1.8,
  ): void {
    series.name = name;

    switch (type) {
      case 'category':
        series.dataFields.categoryX = valueX;
        break;
      case 'date':
        series.dataFields.dateX = valueX;
        if (!(series instanceof am4charts.ColumnSeries)) {
          series.dataItems.template.locations.dateX = 0;
        }
        break;
      case 'value':
        series.dataFields.valueX = valueX;
        break;
    }

    series.dataFields.valueY = valueY;
    series.connect = false;

    if (openValueY) {

      // RANGE SERIES: UPPER AND LOWER BAND WITH FILL
      series.dataFields.openValueY = openValueY;
      series.strokeWidth = 0;
      if (color) {
        series.fill = am4core.color(color);
        series.stroke = am4core.color(color);
      }
      series.sequencedInterpolation = false;
      series.fillOpacity = 0.1;
      series.tensionX = 5;
      series.tooltipText = tooltipText === null ? '[bold]' + name + ': [/]{openValueY.value} to {valueY}' : tooltipText;

    } else {

      // NORMAL SERIES
      if (color) {
        series.fill = am4core.color(color);
        series.stroke = am4core.color(color);
      }
      series.strokeWidth = strokeWidth;
      series.tooltipText = tooltipText === null ? '[bold]' + name + ':[/] {valueY}' : tooltipText;
    }

    // TOOLTIP STUFF
    series.tooltip.background.filters.clear(); // NO SHADOW
    if (defToolTipColor) {
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color(this.BG);
      series.tooltip.background.stroke = am4core.color(this.FONT);
      series.tooltip.label.fill = am4core.color(this.FONT);
    }

  }

}
